import {CharacterSizes} from "@/characters/model/CharacterSizes.ts";
import type Combatant from "@/encounter/model/Combatant.ts";
import type Position from "@/util/Position.ts";
import type CombatantTransform from "@/encounter/model/CombatantTransform.ts";
import {normalizeDegrees} from "@/util/Rotation.ts";

export function translateCombatants(combatants: Combatant[], delta: Position): CombatantTransform[] {
    return combatants.map(combatant => ({
        id: combatant.id,
        position: {
            x: combatant.position.x + delta.x,
            y: combatant.position.y + delta.y,
        },
        rotation: combatant.rotation,
        levelId: combatant.levelId,
    }));
}

export function setCombatantRotation(combatants: Combatant[], rotation: number): CombatantTransform[] {
    return combatants.map(combatant => ({
        id: combatant.id,
        position: combatant.position,
        rotation: normalizeDegrees(rotation),
        levelId: combatant.levelId,
    }));
}

export function getSelectionPivot(combatants: Combatant[]): Position {
    if (combatants.length === 0) return {x: 0, y: 0};

    const bounds = combatants.reduce((current, combatant) => {
        const radius = (CharacterSizes.get(combatant.character.size) ?? 96) / 2;
        return {
            left: Math.min(current.left, combatant.position.x - radius),
            right: Math.max(current.right, combatant.position.x + radius),
            top: Math.min(current.top, combatant.position.y - radius),
            bottom: Math.max(current.bottom, combatant.position.y + radius),
        };
    }, {
        left: Infinity,
        right: -Infinity,
        top: Infinity,
        bottom: -Infinity,
    });

    return {
        x: (bounds.left + bounds.right) / 2,
        y: (bounds.top + bounds.bottom) / 2,
    };
}

export function rotateCombatantsAround(
    combatants: Combatant[],
    pivot: Position,
    deltaDegrees: number,
): CombatantTransform[] {
    const deltaRadians = deltaDegrees * Math.PI / 180;
    const cos = Math.cos(deltaRadians);
    const sin = Math.sin(deltaRadians);

    return combatants.map(combatant => {
        const x = combatant.position.x - pivot.x;
        const y = combatant.position.y - pivot.y;

        return {
            id: combatant.id,
            position: {
                x: pivot.x + cos * x - sin * y,
                y: pivot.y + sin * x + cos * y,
            },
            rotation: normalizeDegrees(combatant.rotation + deltaDegrees),
            levelId: combatant.levelId,
        };
    });
}
